import { useEffect } from "react";
import i18n from "../i18n";
import { buildSeoUrls } from "../i18n/routing";

/**
 * Injects SEO tags directly into <head> via DOM API, side-stepping
 * react-helmet@6's React-18 StrictMode lifecycle issues. All managed tags
 * carry `data-seo-managed="true"` so they are cleaned up on unmount.
 *
 * i18n behaviour:
 *   - If `canonical` is omitted, it is auto-built from `window.location.pathname`
 *     plus the active i18n language and the production canonical domain.
 *   - hreflang `<link rel="alternate">` tags for all 10 supported languages
 *     (nl/en/de/fr/es/it/pl/pt/sv/da) plus an `x-default` pointing at the NL
 *     version are emitted automatically so Google can serve the right locale
 *     per market.
 *
 * Use:
 *   useSeoHead({
 *     title: "...",
 *     description: "...",
 *     // optional — override the auto-generated canonical:
 *     canonical: "https://example.com/exact-url",
 *     meta: [{ property: "og:type", content: "product" }, ...],
 *     jsonLd: [productLd, breadcrumbLd],
 *     // optional — disable hreflang alternates (e.g. admin pages):
 *     skipAlternates: true,
 *   });
 */
export default function useSeoHead({
  title,
  description,
  canonical,
  meta = [],
  jsonLd = [],
  skipAlternates = false,
}) {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const head = document.head;
    const managed = [];

    if (title) {
      const prev = document.title;
      document.title = title;
      managed.push(() => { document.title = prev; });
    }

    const upsertMeta = (selector, attrs) => {
      head.querySelectorAll(selector).forEach((el) => {
        if (!el.dataset.seoOriginal) {
          el.dataset.seoOriginal = el.outerHTML;
        }
        el.remove();
        managed.push(() => {
          if (el.dataset.seoOriginal) {
            const tpl = document.createElement("template");
            tpl.innerHTML = el.dataset.seoOriginal;
            head.appendChild(tpl.content.firstChild);
          }
        });
      });
      const m = document.createElement("meta");
      Object.entries(attrs).forEach(([k, v]) => m.setAttribute(k, v));
      m.dataset.seoManaged = "true";
      head.appendChild(m);
      managed.push(() => m.remove());
    };

    if (description) {
      upsertMeta('meta[name="description"]', { name: "description", content: description });
    }

    // Build the language-aware canonical + alternates
    const pathname = typeof window !== "undefined" ? window.location.pathname : "/";
    const activeLang = (i18n.language || "nl").split("-")[0];
    const { canonical: autoCanonical, alternates } = buildSeoUrls(pathname, activeLang);
    const finalCanonical = canonical || autoCanonical;

    // Remove any stale canonical + alternate links first
    head.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang]').forEach((el) => el.remove());

    const linkCanonical = document.createElement("link");
    linkCanonical.setAttribute("rel", "canonical");
    linkCanonical.setAttribute("href", finalCanonical);
    linkCanonical.dataset.seoManaged = "true";
    head.appendChild(linkCanonical);
    managed.push(() => linkCanonical.remove());

    if (!skipAlternates) {
      alternates.forEach(({ hreflang, href }) => {
        const a = document.createElement("link");
        a.setAttribute("rel", "alternate");
        a.setAttribute("hreflang", hreflang);
        a.setAttribute("href", href);
        a.dataset.seoManaged = "true";
        head.appendChild(a);
        managed.push(() => a.remove());
      });
    }

    meta.forEach((m) => {
      const key = m.property ? "property" : "name";
      const val = m.property || m.name;
      if (!val) return;
      upsertMeta(`meta[${key}="${val}"]`, { [key]: val, content: m.content });
    });

    // og:locale + og:locale:alternate
    if (!skipAlternates) {
      const ogLocaleMap = {
        nl: "nl_NL",
        en: "en_GB",
        de: "de_DE",
        fr: "fr_FR",
        es: "es_ES",
        it: "it_IT",
        pl: "pl_PL",
        pt: "pt_PT",
        sv: "sv_SE",
        da: "da_DK",
      };
      upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: ogLocaleMap[activeLang] || "nl_NL" });
      alternates.forEach(({ hreflang }) => {
        if (hreflang === "x-default" || hreflang === activeLang) return;
        const og = document.createElement("meta");
        og.setAttribute("property", "og:locale:alternate");
        og.setAttribute("content", ogLocaleMap[hreflang] || hreflang);
        og.dataset.seoManaged = "true";
        head.appendChild(og);
        managed.push(() => og.remove());
      });
    }

    jsonLd.forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.seoManaged = "true";
      s.textContent = JSON.stringify(obj);
      head.appendChild(s);
      managed.push(() => s.remove());
    });

    return () => {
      managed.reverse().forEach((fn) => { try { fn(); } catch (e) { /* ignore */ } });
    };
  }, [title, description, canonical, skipAlternates, JSON.stringify(meta), JSON.stringify(jsonLd), i18n.language]);
}
